import React, {useState, useEffect} from "react";
import axios from "axios";
import {Modal, Button, Form, Alert, Spinner, Badge} from "react-bootstrap";
import "./App.css";
import "./antifraud/antifraud.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Honeypot, useAntifraudTicket, withTicketRetry } from "./antifraud";

function App() {
  const APP_API = process.env.REACT_APP_API;
  const APP_TARGET = process.env.REACT_APP_TARGET;
  const LEGAL_DOCUMENT_VERSION =
    process.env.REACT_APP_LEGAL_DOCUMENT_VERSION
    || (process.env.NODE_ENV === "test" ? "2026-08-20" : "");
  const LEGAL_ACCEPTANCE_ENABLED = Boolean(LEGAL_DOCUMENT_VERSION);

  const [showModal, setShowModal] = useState(true); // Открыто ли модальное окно
  const [step, setStep] = useState("email"); // Текущий шаг
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordTwo, setPasswordTwo] = useState("");
  const [fullName, setFullName] = useState("");
  const [schoolName, setSchoolName] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [agreement, setAgreement] = useState(false);
  const [personalData, setPersonalData] = useState(false);
  const [agreementPolicy, setAgreementPolicy] = useState(false);
  const [platformOffer, setPlatformOffer] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSendCode, setIsLoadingSendCode] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [showPassword, setShowPassword] = useState(false); // Показ/скрытие пароля
  const [sentCode, setSentCode] = useState(false);
  const [challengeId, setChallengeId] = useState("");
  const [twoFactorMethod, setTwoFactorMethod] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [isLoadingResend2fa, setIsLoadingResend2fa] = useState(false);
  const [loginRequires2fa, setLoginRequires2fa] = useState(false);
  const [resendAfter, setResendAfter] = useState(0);
  const ticketApi = useAntifraudTicket({
    challengeUrl: APP_API ? `${APP_API}api/v1/antifraud/challenge` : "",
    active: showModal,
  });

  const authErrorText = (err) =>
    err?.response?.data?.detail || "Произошла ошибка. Мы уже разбираемся...";

  const retryAfterFromError = (err) => {
    const value = Number(err?.response?.data?.retry_after);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  };

  const startResendCooldown = (seconds) => {
    if (seconds && seconds > 0) setResendAfter(seconds);
  };

  useEffect(() => {
    const runAutoLogin = async () => {
      if (!APP_API || !APP_TARGET) {
        setIsCheckingSession(false);
        return;
      }

      try {
        const response = await axios.post(
          `${APP_API}api/v1/autologin`,
          {},
          { withCredentials: true },
        );

        if (response.data?.can_login === true && response.data?.auth_code) {
          window.location.href = `${APP_TARGET}${response.data.auth_code}/`;
          return;
        }
      } catch (_error) {
        // Fallback to regular login form when no active session.
      }

      setIsCheckingSession(false);
    };

    runAutoLogin();
  }, [APP_API, APP_TARGET]);

  const resendActive = resendAfter > 0;

  useEffect(() => {
    if (!resendActive) return undefined;
    const timer = window.setInterval(() => {
      setResendAfter((current) => (current > 1 ? current - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendActive]);

  // Валидация email
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  // Обработка отправки email
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError("Пожалуйста, введите правильный email адрес");
      return;
    }
    if (!agreementPolicy) {
      setError("Вы должны ознакомиться с политикой обработки персональных данных");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await axios.post(`${APP_API}api/v1/start`, {
        email,
      });
      console.log(response.data);
      if (response.data.next_step === "login") {
        setLoginRequires2fa(false);
        setStep("login");
      } else {
        setStep("register");
      }
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const completeLogin = (response) => {
    if (response.data.can_login === true && response.data.auth_code) {
      window.location.href = `${APP_TARGET}${response.data.auth_code}/`;
      return;
    }
    setStep("success");
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (loginRequires2fa) {
      if (!twoFactorCode.trim()) {
        setError("Введите код подтверждения");
        return;
      }
      setIsLoading(true);
      setError("");
      try {
        const response = await axios.post(`${APP_API}api/v1/login/2fa/verify`, {
          challenge_id: challengeId,
          code: twoFactorCode,
        });
        completeLogin(response);
      } catch (err) {
        setError(authErrorText(err));
      } finally {
        setIsLoading(false);
      }
      return;
    }
    if (!password) {
      setError("Введите свой пароль");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await withTicketRetry(
        (antifraud) => axios.post(`${APP_API}api/v1/login`, {
          email,
          password,
          antifraud,
        }),
        ticketApi
      );
      if (response.data.next_step === "2fa") {
        setChallengeId(response.data.challenge_id || "");
        setTwoFactorMethod(response.data.method || "email");
        setMaskedEmail(response.data.masked_email || email);
        setTwoFactorCode("");
        setLoginRequires2fa(true);
        return;
      }
      completeLogin(response);
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (
      step === "register"
      && LEGAL_ACCEPTANCE_ENABLED
      && (!agreement || !platformOffer || !personalData)
    ) {
      setError("Для продолжения примите лицензионный договор, публичную оферту и ознакомьтесь с политикой обработки персональных данных");
      return;
    }
    setIsLoadingSendCode(true);
    setError("");
    try {
      const response = await withTicketRetry(
        (antifraud) => axios.post(`${APP_API}api/v1/send_code`, {
          email,
          reset_password: (step === 'forgot'),
          antifraud,
        }),
        ticketApi
      );
      console.log(response.data);
      if (response.data.success === 1) {
        setSentCode(true);
        startResendCooldown(response.data.resend_after_seconds || 90);
      } else {
        setError(response?.data?.detail || "Произошла ошибка. Мы уже разбираемся...");
      }
    } catch (err) {
      if (err?.response?.status === 429 && err?.response?.data?.code === "resend_cooldown") {
        startResendCooldown(retryAfterFromError(err) || 90);
      }
      setError(authErrorText(err));
    } finally {
      setIsLoadingSendCode(false);
    }
  };

  // Обработка регистрации
  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !schoolName || !password || !verificationCode) {
      setError("Пожалуйста, заполните все поля");
      return;
    }
    if (LEGAL_ACCEPTANCE_ENABLED && (!agreement || !platformOffer || !personalData)) {
      setError("Вы должны принять лицензионный договор, публичную оферту и ознакомиться с политикой обработки персональных данных");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await withTicketRetry(
        (antifraud) => axios.post(
          `${APP_API}api/v1/register`,
          {
            email,
            full_name: fullName,
            school_name: schoolName,
            password: password,
            code: verificationCode,
            antifraud,
            agreements: LEGAL_ACCEPTANCE_ENABLED
              ? [
                  {
                    document_code: "license_agreement",
                    document_version: LEGAL_DOCUMENT_VERSION,
                    accepted: agreement,
                  },
                  {
                    document_code: "platform_offer",
                    document_version: LEGAL_DOCUMENT_VERSION,
                    accepted: platformOffer,
                  },
                  {
                    document_code: "personal_data",
                    document_version: LEGAL_DOCUMENT_VERSION,
                    accepted: personalData,
                  },
                ]
              : [],
          }
        ),
        ticketApi
      );
      ticketApi.markUsed();
      if (response.data.can_login === true) {
        window.location.href = `${APP_TARGET}${response.data.auth_code}/`;
        // alert(`https://site.com/auth/${response.data.auth_code}/`);
      }
      else {
        setStep("success");
      }
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoading(false);
    }
  };


  const handleResend2fa = async (e) => {
    e.preventDefault();
    setIsLoadingResend2fa(true);
    setError("");
    try {
      await axios.post(`${APP_API}api/v1/login/2fa/resend`, {
        challenge_id: challengeId,
      });
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoadingResend2fa(false);
    }
  };

  const handleEmailFallback2fa = async (e) => {
    e.preventDefault();
    setIsLoadingResend2fa(true);
    setError("");
    try {
      const response = await axios.post(`${APP_API}api/v1/login/2fa/email-fallback`, {
        challenge_id: challengeId,
      });
      setTwoFactorMethod(response.data.method || "email");
      if (response.data.masked_email) {
        setMaskedEmail(response.data.masked_email);
      }
    } catch (err) {
      setError(authErrorText(err));
    } finally {
      setIsLoadingResend2fa(false);
    }
  };

  const handleForgotPassword = () => {
    setError("");
    setLoginRequires2fa(false);
    setStep('forgot');
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    if (!password || !passwordTwo) {
      setError('Пожалуйста, введите новый пароль в оба поля');
    }
    else if (password !== passwordTwo) {
      setError('Пароли не совпадают');
    }
    else {
      setIsLoading(true);
      setError("");
      setPassword('');
      setPasswordTwo('');
      try {
        await axios.post(
          `${APP_API}api/v1/reset`,
          {
            email,
            password: password,
            code: verificationCode,
          }
        );
        setStep('login');
        setLoginRequires2fa(false);
      } catch (err) {
        setError(authErrorText(err));
      } finally {
        setIsLoading(false);
      }
    }

  }

  if (isCheckingSession) {
    return (
      <div className="App d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  return (
    <div className="App">
      {/* Модальное окно */}
      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" centered>
        <Modal.Header>
          <Modal.Title>Ednice авторизация</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5>Регистрация и авторизация владельцев онлайн-школ</h5>
              <div>
                {/* Шаг 1: Ввод email */}
                {step === "email" && (
                  <Form onSubmit={handleEmailSubmit}>
                    <Form.Group controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Введите свой email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="agreement" className="mt-3">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            Я ознакомился с{" "}
                            <a href="https://ednice.ru/about/privacy-policy">политикой обработки персональных данных</a>
                          </>
                        }
                        checked={agreementPolicy}
                        onChange={(e) => setAgreementPolicy(e.target.checked)}
                      />
                    </Form.Group>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || !agreementPolicy}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Продолжить"
                      )}
                    </Button>
                  </Form>
                )}

                {/* Шаг 2.1: Ввод пароля */}
                {step === "login" && (
                  <Form onSubmit={handleLoginSubmit}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Badge bg="info" as={"h6"}>Email: {email}</Badge>
                    {!loginRequires2fa ? (
                      <Alert variant="primary">Вы уже зарегистрированы у нас. Введите пароль для входа.</Alert>
                    ) : (
                      <Alert variant="primary">
                        У вас включена двухфакторная аутентификация. Введите код подтверждения для завершения входа.
                        {twoFactorMethod === "totp" ? (
                          <> Откройте приложение-аутентификатор и введите текущий код.</>
                        ) : (
                          <> Мы отправили код на {maskedEmail || email}.</>
                        )}
                      </Alert>
                    )}
                    <Form.Group controlId="password">
                      <Form.Label column sm={12}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>Пароль</span>
                          {!loginRequires2fa ? (
                            <a
                              href="#forgot-password"
                              onClick={(e) => {
                                e.preventDefault();
                                handleForgotPassword();
                              }}
                              style={{ textDecoration: 'none', cursor: 'pointer' }}
                            >
                              Не помню пароль
                            </a>
                          ) : null}
                        </div>
                      </Form.Label>
                      <div className="password-input">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          placeholder="Введите свой пароль"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={loginRequires2fa}
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password"
                          disabled={loginRequires2fa}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                    </Form.Group>
                    {loginRequires2fa ? (
                      <>
                        <Form.Group controlId="twoFactorCode" className="mt-3">
                          <Form.Label>Код подтверждения</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Введите код"
                            value={twoFactorCode}
                            onChange={(e) => setTwoFactorCode(e.target.value)}
                            required
                            autoFocus
                          />
                        </Form.Group>
                        {twoFactorMethod === "email" ? (
                          <Button
                            variant="link"
                            className="p-0 mb-2"
                            disabled={isLoadingResend2fa}
                            onClick={handleResend2fa}
                          >
                            {isLoadingResend2fa ? "Отправка..." : "Отправить код ещё раз"}
                          </Button>
                        ) : (
                          <Button
                            variant="link"
                            className="p-0 mb-2"
                            disabled={isLoadingResend2fa}
                            onClick={handleEmailFallback2fa}
                          >
                            {isLoadingResend2fa ? "Отправка..." : "Не могу ввести код — прислать на email"}
                          </Button>
                        )}
                      </>
                    ) : null}
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Войти"
                      )}
                    </Button>
                  </Form>
                )}

                {/* Шаг 2.2: Регистрация */}
                {step === "register" && (
                  <Form onSubmit={handleRegisterSubmit}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Badge bg="info" as={"h6"}>Пользователь с таким email не найден: {email}</Badge>
                    <Alert variant="primary">
                      Пожалуйста, заполните регистрационную информацию о себе. <br />
                      Вы регистрируетесь как владелец онлайн-школы. Если вы сотрудник или спикер и не уполномочены управлять онлайн-школой, то попросите владельца добавить вас в список сотрудников
                    </Alert>
                    <Form.Group controlId="fullName">
                      <Form.Label>Ваше полное имя</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="*"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="schoolName" className="mt-3">
                      <Form.Label>Название вашей онлайн-школы</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="*"
                        value={schoolName}
                        onChange={(e) => setSchoolName(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group controlId="password" className="mt-3">
                      <Form.Label>Пароль</Form.Label>
                      <div className="password-input">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          aria-describedby="passwordHelpBlock"
                          placeholder="*"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                      <Form.Text id="passwordHelpBlock" muted>
                        Пароль должен быть не менее 6 символов и содержать только латинские буквы и цифры
                      </Form.Text>
                    </Form.Group>
                    <Form.Group controlId="verificationCode" className="mt-3">
                      <Form.Label>Проверочный код</Form.Label>
                      {sentCode ? (<><Form.Control
                        type="text"
                        aria-describedby="verificationCodeHelpBlock"
                        placeholder="*"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                      />
                      <Form.Text id="verificationCodeHelpBlock" muted>
                        Мы выслали проверочный код на email <b>{email}</b>. Проверьте почту и введите код в это поле
                      </Form.Text></>) : null}
                      <Button
                      variant="primary"
                      className="w-100 mt-3"
                      onClick={handleSendCode}
                      disabled={isLoadingSendCode || resendAfter > 0}
                    >
                      {isLoadingSendCode ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : resendAfter > 0 ? (
                        `Повторно через ${resendAfter} с`
                      ) : sentCode ? (
                        "Отправить код ещё раз"
                      ) : (
                        "Отправить код на email"
                      )}
                    </Button>
                    </Form.Group>
                    {LEGAL_ACCEPTANCE_ENABLED && (
                      <>
                        <Form.Group controlId="agreement" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label={
                              <>
                                Я согласен с условиями{" "}
                                <a href="https://ednice.ru/about/license-agreement">лицензионного договора</a>
                              </>
                            }
                            checked={agreement}
                            onChange={(e) => setAgreement(e.target.checked)}
                          />
                        </Form.Group>
                        <Form.Group controlId="platformOffer" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label={
                              <>
                                Я согласен с условиями{" "}
                                <a href="https://ednice.ru/about/offer/">публичной оферты платформы</a>
                              </>
                            }
                            checked={platformOffer}
                            onChange={(e) => setPlatformOffer(e.target.checked)}
                          />
                        </Form.Group>
                        <Form.Group controlId="personalData" className="mt-3">
                          <Form.Check
                            type="checkbox"
                            label={
                              <>
                                Я ознакомился с{" "}
                                <a href="https://ednice.ru/about/personal-data/">
                                  политикой обработки персональных данных
                                </a>
                              </>
                            }
                            checked={personalData}
                            onChange={(e) => setPersonalData(e.target.checked)}
                          />
                        </Form.Group>
                      </>
                    )}
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        isLoading
                        || !sentCode
                        || (LEGAL_ACCEPTANCE_ENABLED && (!agreement || !platformOffer || !personalData))
                      }
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Регистрация"
                      )}
                    </Button>
                  </Form>
                )}

                {/* Шаг 3: Success */}
                {step === "success" && (
                  <>
                  <Alert variant={"success"}>
                    Благодарим вас за регистрацию в качестве владельца онлайн-школы на платформе Эднайс.
                  </Alert>
                  <Alert variant={"secondary"}>
                    В настоящий момент наш сервис работает в режиме закрытого доступа и добавление новых
                    участников осуществляется в ручном режиме. <br />
                    Мы одобрим вашу заявку, как только появятся свободные слоты. <br />
                    А пока рекомендуем вам зайти в нашу группу в Телеграм, где мы совместно обсуждаем запуск,
                    работоспособность и прочие аспекты работы платформы, чтобы не пропустить ничего важного. <br />
                    <a href="https://t.me/+y-8TvIDnYR84MWFi" target="_blank" rel="noopener noreferrer">https://t.me/+y-8TvIDnYR84MWFi</a>
                  </Alert>
                  </>
                )}

                {/* Шаг 2.3: Forgot password */}
                {step === "forgot" && (
                  <Form onSubmit={handleSetNewPassword}>
                    <Honeypot {...ticketApi.honeypotProps} />
                    {ticketApi.powSlot}
                    <Badge bg="info" as={"h6"}>Email: {email}</Badge>
                    <Alert variant="primary">Вы уже зарегистрированы у нас и хотите установить новый пароль</Alert>
                    <Form.Group controlId="password" className="mt-3">
                      <Form.Label>Новый пароль</Form.Label>
                      <div className="password-input">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          aria-describedby="passwordHelpBlock"
                          placeholder="********"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                      <Form.Text id="passwordHelpBlock" muted>
                        Пароль должен быть не менее 6 символов и содержать только латинские буквы и цифры
                      </Form.Text>
                    </Form.Group>
                    <Form.Group controlId="password2" className="mt-3">
                      <Form.Label>Новый пароль ещё раз</Form.Label>
                      <div className="password-input">
                        <Form.Control
                          type={showPassword ? "text" : "password"}
                          aria-describedby="password2HelpBlock"
                          placeholder="********"
                          value={passwordTwo}
                          onChange={(e) => setPasswordTwo(e.target.value)}
                          required
                        />
                        <Button
                          variant="outline-secondary"
                          onClick={() => setShowPassword(!showPassword)}
                          className="toggle-password"
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </Button>
                      </div>
                    </Form.Group>
                    <Form.Group controlId="verificationCode" className="mt-3">
                      <Form.Label>Проверочный код</Form.Label>
                      {sentCode ? (<><Form.Control
                        type="text"
                        aria-describedby="verificationCodeHelpBlock"
                        placeholder="*"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        required
                      />
                      <Form.Text id="verificationCodeHelpBlock" muted>
                        Мы выслали проверочный код на email <b>{email}</b>. Проверьте почту и введите код в это поле
                      </Form.Text></>) : null}
                      <Button
                      variant="primary"
                      className="w-100 mt-3"
                      onClick={handleSendCode}
                      disabled={isLoadingSendCode || resendAfter > 0}
                    >
                      {isLoadingSendCode ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : resendAfter > 0 ? (
                        `Повторно через ${resendAfter} с`
                      ) : sentCode ? (
                        "Отправить код ещё раз"
                      ) : (
                        "Отправить код на email"
                      )}
                    </Button>
                    </Form.Group>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isLoading || !sentCode}
                      className="w-100 mt-3"
                    >
                      {isLoading ? (
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        "Установить новый пароль"
                      )}
                    </Button>
                  </Form>
                )}
              </div>

          {/* Отображение ошибок */}
          {error && (
            <Alert variant="danger" className="mt-3">
              {error}
            </Alert>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default App;